import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
// Models
import { bindToContent, CmsEntities } from 'client/data/models/cms';

// Utils
import { connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { getStaticImageUrl } from 'client/utils/image-helpers';

// Shared components
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { PhotoflipperVideoImageTopRated } from 'site-modules/shared/components/editorial/photoflipper-video-image-top-rated/photoflipper-video-image-top-rated';

// Components
import { EditorsList } from 'site-modules/editorial/components/editors-list/editors-list';

import './editors.scss';

export const ABOUT_EDITORS_CONTENT_FILE_PATH = 'research/about/editors/index-new-design';

function EditorsUI({ content = null, isMobile = false }) {
  if (!content) {
    return null;
  }

  const pathToImages = getStaticImageUrl('img/about/editors-assets');

  const { title } = content;
  const summary = content.metadata('summary').value();

  const { title: missionTitle, summary: missionSummary } = content.child('mission');
  const { title: transparencyTitle, summary: transparencySummary } = content.child('transparency');
  const { title: reviewsTitle, summary: reviewsSummary } = content.child('reviews');
  const { title: experienceTitle, summary: experienceSummary } = content.child('experience');

  const editorsSections = content
    .child('editors')
    .children()
    .map(section => ({ ...section, hash: section.title.toLowerCase().replace(/\s/g, '-') }));

  const firstPhotoCarousel = content.child('first-photo-carousel').links();
  const secondPhotoCarousel = content.child('second-photo-carousel').links();

  const galleryCarouselProps = {
    wrapperClass: 'mt-3_5 mb-3_5',
    fullImageSize: 'max',
    isCenterMode: true,
    centerPadding: isMobile ? '7%' : '20%',
    slickSlideClassName: 'road-test-gallery',
    isPreHovered: isMobile,
    showMeatballs: false,
  };

  return (
    <main id="main-content" tabIndex={-1} className="use-glass-theme editors overflow-hidden">
      <div className="hero-content pt-1 pt-md-3_5 px-0_5 px-md-0 pb-3_5">
        <Container>
          <section className="vehicle-testing rounded-16 mb-3_5">
            <Row className="align-items-center justify-content-center g-0 ps-md-1_5 ps-xl-3">
              <Col xs={12} md={6} className="px-1 pt-1_5 px-md-0 pt-md-0">
                <ContentFragment componentToUse="h1" classes="fw-bold mb-1">
                  {title}
                </ContentFragment>
                <ContentFragment componentToUse="p" classes="mb-0">
                  {summary}
                </ContentFragment>
              </Col>
              <Col tag="picture" xs={12} md={6}>
                <img className="hero-image w-100 pos-r" src={`${pathToImages}/hero.webp`} alt="" fetchPriority="high" />
              </Col>
            </Row>
          </section>
        </Container>

        <Container className="mission-content px-0 px-lg-3_5">
          <div className="d-flex flex-column-reverse flex-md-row px-0 px-lg-0_75 pos-r">
            <img className="mission-img w-100" src={`${pathToImages}/edmunds_mission.webp`} alt="" />
            <div className="d-flex flex-column justify-content-center mt-3 mt-md-0 mb-1 mb-md-0 ps-md-3_5 mx-0_5">
              <ContentFragment componentToUse="h2" classes="mission-title fw-bold mb-1_5">
                {missionTitle}
              </ContentFragment>
              <ContentFragment classes="size-20">{missionSummary}</ContentFragment>
            </div>
          </div>

          <div className="pos-r d-flex flex-column align-items-center flex-md-row align-items-md-start justify-content-md-between pt-3_5 pb-3_5 pt-md-0 pb-md-0 px-0_75">
            <img
              className="pos-a center-x top-0 hidden-md-up"
              width="46%"
              height="130%"
              src={`${pathToImages}/vertical-lines.svg`}
              alt=""
            />
            <div className="pos-r w-50 hidden-md-down">
              <img
                className="pos-a left-lines-bottom"
                width="108%"
                src={`${pathToImages}/left-lines-bottom.svg`}
                alt=""
              />
            </div>
            <div className="img-container pos-r d-flex align-items-end">
              <img className="transparency-img pos-a" src={`${pathToImages}/transparency.webp`} alt="" />
              <div className="text-container pos-r text-white d-flex flex-column justify-content-end px-1_5 px-lg-2 pb-1_25 pb-lg-1_75">
                <ContentFragment componentToUse="span" classes="img-header size-36 fw-bold mb-0_5">
                  {transparencyTitle}
                </ContentFragment>
                <ContentFragment componentToUse="span">{transparencySummary}</ContentFragment>
              </div>
            </div>
          </div>
          <div className="pos-r d-flex flex-column flex-md-row justify-content-md-between pt-3_5 pb-3 pt-md-0 pb-md-0 px-0 px-md-0_75">
            <img
              className="pos-a vertical-bottom-line hidden-md-up"
              width="46%"
              height="100%"
              src={`${pathToImages}/vertical-lines.svg`}
              alt=""
            />
            <div className="img-container pos-r d-flex align-items-end">
              <img className="review-img pos-a" src={`${pathToImages}/review.webp`} alt="" />
              <div className="text-container pos-r text-white d-flex flex-column justify-content-end px-1_5 px-lg-2 pb-1_25 pb-lg-1_75">
                <ContentFragment componentToUse="span" classes="img-header size-36 fw-bold mb-0_5">
                  {reviewsTitle}
                </ContentFragment>
                <ContentFragment componentToUse="span">{reviewsSummary}</ContentFragment>
              </div>
            </div>
            <div className="img-container pos-r hidden-md-down">
              <img
                className="pos-a center-x"
                width="46%"
                height="115%"
                src={`${pathToImages}/vertical-lines.svg`}
                alt=""
              />
            </div>
          </div>
          <div className="d-flex flex-column align-items-end flex-md-row px-0_75 pt-3 pb-1 pt-md-2 pb-md-0 mt-md-3_5 ">
            <div className="pos-r w-50 hidden-md-down">
              <img className="pos-a w-100 bottom-0 left-lines-top" src={`${pathToImages}/left-lines-top.svg`} alt="" />
            </div>
            <div className="img-container pos-r d-flex align-items-end">
              <img className="experience-img pos-a" src={`${pathToImages}/experience.webp`} alt="" />
              <div className="text-container experience-text pos-r text-white d-flex flex-column justify-content-end px-1_5 px-lg-2">
                <ContentFragment componentToUse="span" classes="img-header size-36 fw-bold mb-0_5">
                  {experienceTitle}
                </ContentFragment>
                <ContentFragment componentToUse="span">{experienceSummary}</ContentFragment>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <div className="editors-content pt-1 pt-md-3_5 px-0_5 px-md-0">
        <Container>
          {editorsSections.slice(0, 1).map(editorsSection => (
            <EditorsList editorsSection={editorsSection} key={editorsSection.id} />
          ))}
        </Container>

        <PhotoflipperVideoImageTopRated {...galleryCarouselProps} vehicleGallery={firstPhotoCarousel} />

        <Container>
          {editorsSections.slice(1).map(editorsSection => (
            <EditorsList editorsSection={editorsSection} key={editorsSection.id} />
          ))}
        </Container>

        <PhotoflipperVideoImageTopRated {...galleryCarouselProps} vehicleGallery={secondPhotoCarousel} />
      </div>
    </main>
  );
}

EditorsUI.propTypes = {
  content: CmsEntities.Content,
  isMobile: PropTypes.bool,
};

export const Editors = connect(state => ({ isMobile: state.mobile }))(
  connectToModel(EditorsUI, {
    content: bindToContent(ABOUT_EDITORS_CONTENT_FILE_PATH),
  })
);
